import { Input, Tooltip, Typography } from "antd-v5";
import { FINDING_SEVERITY_BREAKDOWN_WIDTH } from "constants/Layout";
import useActiveSeveritiesKeys from "hooks/useActiveSeveritiesKeys";
import useDebounceCallback from "hooks/useDebounceCallback";
import useDetectFilterChange from "hooks/useDetectFilterChange";
import useFindingColumns from "hooks/useFindingColumns";
import { compact, find, isEmpty, noop } from "lodash";
import React from "react";
import { FindingSummary } from "types/api/findingSummary";
import { getUrlSearch } from "utils/urlUtil";
import { CommonTableProps } from "views/Findings/FindingList/common";

import ACButtonGroup, { ACButtonGroupItem } from "ac-components/ACButtonGroup";
import ACPaginationBar from "ac-components/ACPaginationBar/ACPaginationBar";
import ACTable from "ac-components/ACTable/ACTable";
import ACColumnDragWrapper from "ac-components/ac-column-drag-wrapper/ACColumnDragWrapper";
import ACColumnVisibilityDropdown from "ac-components/ac-column-visibility-dropdown/ACColumnVisibilityDropdown";
import ACFindingBreakdownWrapper from "ac-components/ac-finding-breakdown-wrapper/ACFindingBreakdownWrapper";
import ColumnsIcon from "ac-components/ac-icons/ColumnsIcon";
import LoadingIcon from "ac-components/ac-icons/LoadingIcon";
import SearchIcon from "ac-components/ac-icons/SearchIcon";
import ViewFindingIcon from "ac-components/ac-icons/ViewInFindingIcon";

import {
  columnKeys,
  useAssetFindingListDetails,
} from "./AssetFindingListProvider";

export default function AssetFindingListTable() {
  const {
    tableCustomizationHelper,
    dataSource,
    activeColumns,
    loading,
    paginationControls,
    totalElements,
    assetID,
    stats,
  } = useAssetFindingListDetails();
  const {
    page,
    pageSize,
    onPageChange,
    onSortChange,
    sortInfo,
    filterInfo,
    onFilterChange,
  } = paginationControls;

  const {
    columns: customizedColumns,
    onColumnOrderChange,
    alwaysVisibleKeys,
  } = tableCustomizationHelper;

  const columns = useFindingColumns({
    showFindingDetail: noop,
    externalColumn: [],
  });

  const allColumns = compact(
    columnKeys.map((key: string) => {
      return find(columns, { key });
    }),
  );

  const onSearch = useDebounceCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      onFilterChange({
        title: isEmpty(value) ? [] : [value],
      });
    },
    [onFilterChange],
    1000,
  );

  const actualAppliedFilters = useDetectFilterChange(filterInfo);
  const activeSeverities = useActiveSeveritiesKeys();

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center"
        style={{ padding: "16px 24px" }}
      >
        <Input
          prefix={<SearchIcon />}
          placeholder="Search findings by summary"
          style={{ width: 256 }}
          onChange={onSearch}
          allowClear
        />
        {!stats ? (
          <LoadingIcon />
        ) : (
          <span style={{ width: FINDING_SEVERITY_BREAKDOWN_WIDTH }}>
            <ACFindingBreakdownWrapper
              findingSeverity={stats}
              activeSeverityKeys={activeSeverities}
              clickable={false}
            />
          </span>
        )}
        <ACButtonGroup size="md">
          <ACColumnVisibilityDropdown
            columns={allColumns}
            columnVisibilityConfig={
              tableCustomizationHelper.columnVisibilityConfig
            }
            fixedColumns={alwaysVisibleKeys.length}
            onChange={tableCustomizationHelper.onColumnVisibilityChange}
            onColumnOrderChange={tableCustomizationHelper.onColumnOrderChange}
            onResetColumnOptions={tableCustomizationHelper.onResetColumnOptions}
            activeColumns={activeColumns}
            alwaysVisibleKeys={tableCustomizationHelper.alwaysVisibleKeys}
          >
            <Tooltip title="Manage Columns">
              <ACButtonGroupItem>
                <ColumnsIcon />
              </ACButtonGroupItem>
            </Tooltip>
          </ACColumnVisibilityDropdown>
          <Tooltip title="View in Findings">
            <Typography.Link
              onClick={(e) => {
                e.stopPropagation();
              }}
              // @ts-ignore
              href={`/#/findings?${getUrlSearch({
                filters: {
                  assetId: [assetID],
                },
              })}`}
              target="_blank"
            >
              <ACButtonGroupItem>
                <ViewFindingIcon />
              </ACButtonGroupItem>
            </Typography.Link>
          </Tooltip>
        </ACButtonGroup>
      </div>
      <ACColumnDragWrapper onDragEnd={onColumnOrderChange}>
        <ACTable<FindingSummary>
          filterInfo={actualAppliedFilters}
          onFilterChange={onFilterChange}
          sortInfo={sortInfo}
          onSortChange={onSortChange}
          columns={customizedColumns}
          dataSource={dataSource}
          loading={loading}
          pagination={{ pageSize }}
          {...CommonTableProps}
          fullPage
          customizationHelpers={tableCustomizationHelper}
        />
      </ACColumnDragWrapper>
      <ACPaginationBar
        className="ac-finding-list-pagination-bar"
        size="md"
        total={totalElements}
        page={page}
        onChange={onPageChange}
        pageSize={pageSize}
        showSizeChanger={false}
        showEdges={false}
        bordered={false}
      />
    </>
  );
}
